<template>
    <BaseDialog v-model:open="isDocumentUploadDialogOpen" :title="dialogTitle" @close="handleCancel" @interact-outside="handleCancel">
        <div class="flex flex-col gap-4 body text-ink-muted">
            <p>
                {{ t("moments.documents.dialog.intro") }} <span v-if="uploadsExamples">{{ t("moments.documents.dialog.examplesLabel") }}</span>
            </p>
            <ul v-if="uploadsExamples" class="list-disc ml-5 mb-3">
                <li v-for="example in uploadsExamples" :key="example">{{ example }}</li>
            </ul>
            <AdditionalDocumentUploadSection
                v-if="isDocumentUploadDialogOpen"
                :is-disabled="isLinkingDocuments"
                :upload-type="DOCUMENT_UPLOAD_TYPES.OTHER"
                @uploading="isUploading = $event"
                @document-uploaded="handleDocumentUploaded"
                @document-removed="handleDocumentRemoved"
                @document-upload-error="handleDocumentUploadError"
                @document-selected="handleDocumentSelected" />
        </div>
        <template #footer>
            <BaseButton theme="neutral" :disabled="isLinkingDocuments" @click="handleCancel">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="primary" :disabled="isSubmitting || isUploading" class="flex gap-2 items-center" @click="handleStartDrafting">
                <LoadingSpinner v-if="isSubmitting" class="h-4" />
                {{ t("moments.documents.dialog.startChat") }}
            </BaseButton>
        </template>
        <BaseToast v-model:open="isErrorToastOpen" :description="errorMessage" />
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ChatInputFile } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AdditionalDocumentUploadSection from "~vue/Moments/AdditionalDocumentUploadSection.vue";
import { usePerformanceReviewDocumentEvents } from "~vue/Moments/composables/usePerformanceReviewDocumentEvents";
import { DOCUMENT_UPLOAD_TYPES } from "~vue/Moments/constants.js";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface UploadedDocument {
    id: number;
    upload_type: string;
}

const { review } = defineProps<{
    review: DecoratedPerformanceReview;
}>();
const emit = defineEmits(["save", "cancel"]);

const dialogTitle = computed(() => t("moments.documents.dialog.title"));
const uploadsExamples = computed(() => {
    if (review.cta?.uploads_examples?.length) {
        return review.cta.uploads_examples;
    }
    return [t("moments.documents.dialog.examples.goals"), t("moments.documents.dialog.examples.feedbackNotes"), t("moments.documents.dialog.examples.previousReview")];
});

const reviewType = computed(() => review.cta?.reviewer_target ?? "self");

const isDocumentUploadDialogOpen = defineModel("dialog", {
    type: Boolean,
    required: true,
});

const {
    handleDocumentSelected,
    handleDocumentUploadError,
    handleDocumentRemoved: logDocumentRemoved,
    handleSubmitted,
} = usePerformanceReviewDocumentEvents("review_document", review.id, reviewType);

// The "uploaded" analytics event is emitted server-side by the link_documents endpoint, so this
// handler only maintains local state.
const handleDocumentUploaded = (documents: UploadedDocument | Array<UploadedDocument>) => {
    if (Array.isArray(documents)) {
        uploadedDocuments.value.push(...documents);
    } else {
        uploadedDocuments.value.push(documents);
    }
};

const handleDocumentRemoved = (document: ChatInputFile) => {
    if (!("id" in document)) {
        return;
    }
    uploadedDocuments.value = uploadedDocuments.value.filter((doc) => doc.id !== document.id);
    logDocumentRemoved(document);
};

const isSubmitting = ref(false);
const isUploading = ref(false);
const isErrorToastOpen = ref(false);
const errorMessage = ref("");
const uploadedDocuments = ref<Array<UploadedDocument>>([]);

const linkUrl = computed(() => `/api/coach/performance_reviews/${review.id}/documents/`);
const documentPayload = computed(() => ({
    documents: uploadedDocuments.value.map((doc) => ({ file_id: doc.id, upload_type: doc.upload_type })),
    dialog: "review_document",
}));
const { execute: linkDocuments, isFetching: isLinkingDocuments } = useFetch(linkUrl, { immediate: false }).post(documentPayload).json();

const handleStartDrafting = async () => {
    if (isSubmitting.value || isUploading.value) {
        return;
    }
    isSubmitting.value = true;
    if (uploadedDocuments.value.length > 0) {
        try {
            await linkDocuments(true);
            handleSubmitted(uploadedDocuments.value);
        } catch {
            errorMessage.value = t("moments.documents.dialog.linkFailed");
            isErrorToastOpen.value = true;
            isSubmitting.value = false;
            return;
        }
    }
    isSubmitting.value = false;
    emit("save", uploadedDocuments.value.length);
    uploadedDocuments.value = [];
    isDocumentUploadDialogOpen.value = false;
};

const handleCancel = () => {
    isDocumentUploadDialogOpen.value = false;
    isUploading.value = false;
    uploadedDocuments.value = [];
    emit("cancel");
};
</script>
